import {
  Box,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Typography,
  Chip,
  Badge,
  Fade,
  Skeleton,
  CircularProgress,
} from "@mui/material";
import React, { useState, useRef, useContext, useEffect } from "react";
import { Link } from "react-router-dom";
import StarRating from "./StarRating";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import StarIcon from "@mui/icons-material/Star";
import ImageCarousel from "../../common/ImageCarousel";
import CustomButton from "../../common/Button/CustomButton";
import CartItemsContext from "../../../context/cartItems/CartItemsContext";
import LikedItemsContext from "../../../context/likedItems/LikedItemsContext";
import CustomisedSnackbar from "../../common/Snackbar/CustomisedSnackbar";
import AllProductContext from "../../../context/allProducts/AllProductsContext";
import "./styles.css";
import metaPixel from "../../../utils/analytics/metaPixel";

const ProductCard = ({
  product,
  link,
  addToCartButton,
  showCarousel,
  fadeOutOnRemovingLike = false,
  showLikeButton = true,
  useMobileSize = false,
  showRating = true,
  showRatingNumber = true,
  showQuantity = false,
  compact = false,
}) => {
  const initRating = {
    avg: 0,
    total: 0,
  };
  const [open, setOpen] = useState(false);
  const [liked, setLiked] = useState(false);
  const [rating, setRating] = useState(initRating);
  const [isHovered, setIsHovered] = useState(false);
  const [cartQuantity, setCartQuantity] = useState(0);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [isRemovingFromCart, setIsRemovingFromCart] = useState(false);
  const [isMobileScreen, setIsMobileScreen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success"); // Add this state
  const snackbarMessage = useRef("");
  const cartItemsContext = useContext(CartItemsContext);
  const likedItemsContext = useContext(LikedItemsContext);
  const allProductContext = useContext(AllProductContext);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [loaded, setLoaded] = useState(false);

  // Screen size detection
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobileScreen(window.innerWidth <= 768);
    };

    // Check initial screen size
    checkScreenSize();

    // Add event listener for window resize
    window.addEventListener("resize", checkScreenSize);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener("resize", checkScreenSize);
    };
  }, []);

  // Use detected mobile screen or prop value
  const effectiveUseMobileSize = isMobileScreen || useMobileSize;

  const loadImage = (image) => {
    return new Promise((resolve, reject) => {
      const loadImg = new Image();
      loadImg.src = image;
      // wait 1 second to simulate loading time
      loadImg.onload = () =>
        // setTimeout(() => {
        {
          // console.log("resolve:", image, ", proId:", product.id);
          resolve(image);
        };
      // }, 1000);

      loadImg.onerror = (err) => reject(err);
    });
  };
  useEffect(() => {
    const allProductsArr = allProductContext.state;

    // console.log(
    //   "Product Card =====> allProductContext.state: ",
    //   allProductsArr
    // );
    setRating(initRating);
    if (allProductsArr !== null && allProductsArr.length > 0) {
      const prod = allProductsArr.filter((item) => {
        return item.id === product.id;
      });
      if (!prod || prod.length === 0) return;
      if (prod[0].ratings && prod[0].ratings.length > 0) {
        const p_rating_average =
          prod[0].ratings.reduce((a, b) => a + b, 0) / prod[0].ratings.length;
        const rating_average = Math.round(p_rating_average / 0.5) * 0.5;
        const setData = {
          avg: rating_average,
          total: prod[0].ratings.length,
        };
        // console.log("UPDATE PRO RATING====>", setData);
        setRating({
          ...setData,
        });
      } else {
        setRating({ ...initRating });
      }
    }
  }, [allProductContext.state]);

  useEffect(() => {
    setLiked(false);
    if (
      likedItemsContext.state !== null &&
      likedItemsContext.state.length > 0
    ) {
      const likedItemsArray = likedItemsContext.state;
      const indexOfProductInLikedItems = likedItemsArray.findIndex(
        (a) => a.id === product.id
      );
      // console.log("likedItemsArray:", likedItemsArray.length);

      if (indexOfProductInLikedItems !== -1) {
        setLiked(true);
      }
    }
  }, [likedItemsContext.state]);

  useEffect(() => {
    if (!product || !product.images || product.images.length === 0) return;
    loadImage(product.images[0].url)
      .then(() => setLoaded(true))
      .catch((err) => setLoaded(true));
    setDeleteItemId(null);
    setLiked(false);
    if (likedItemsContext.state !== null) {
      const likedItemsArray = likedItemsContext.state;
      const indexOfProductInLikedItems = likedItemsArray.findIndex(
        (a) => a.id === product.id
      );
      if (indexOfProductInLikedItems !== -1) {
        setLiked(true);
      }
    }
    if (product.ratings && product.ratings.length > 0) {
      const p_rating_average =
        product.ratings.reduce((a, b) => a + b, 0) / product.ratings.length;
      const rating_average = Math.round(p_rating_average / 0.5) * 0.5;
      const setData = {
        avg: rating_average,
        total: product.ratings.length,
      };
      // console.log("UPDATE PRO RATING====>", setData);
      setRating({
        ...setData,
      });
    } else {
      setRating({ ...initRating });
    }
  }, [product.id]);
  useEffect(() => {
    if (deleteItemId !== null) {
      setTimeout(() => {
        setOpen(true);
      }, 500);
    }
  }, [deleteItemId]);

  const handleAddToCart = () => {
    if (isAddingToCart) return; // Prevent action if disabled

    // Never let the card push the cart past available stock
    const availableUnits = Number(product.unitsInStock);
    if (Number.isFinite(availableUnits) && cartQuantity >= availableUnits) {
      return;
    }

    setIsAddingToCart(true); // Disable button
    setTimeout(() => setIsAddingToCart(false), 1000); // Re-enable after 1 second

    const tempCartItems =
      cartItemsContext.state !== null ? cartItemsContext.state : [];

    const indexOfProductInCart = tempCartItems.findIndex(
      (a) => a.id === product.id
    );

    // Check if this product already added to cart

    // console.log("indexOfProductInCart:", indexOfProductInCart);
    if (indexOfProductInCart !== -1) {
      const products_ = [...tempCartItems];
      products_.splice(indexOfProductInCart, 1);
      const tempItemCount = tempCartItems[indexOfProductInCart].quantity;
      cartItemsContext.setState([
        { ...product, quantity: tempItemCount + 1 },
        ...products_,
      ]);
      snackbarMessage.current =
        "1 more unit of " + product.title + " added to cart";
    } else {
      snackbarMessage.current = "1 unit of " + product.title + " added to cart";
      cartItemsContext.setState([
        { ...product, quantity: 1 },
        ...tempCartItems,
      ]);
    }
    metaPixel.addToCart(product);
    setSnackbarSeverity("success"); // Set green color for add actions
    setOpen(true);
  };

  // Add new function to handle removing items from cart
  const handleRemoveFromCart = () => {
    if (isRemovingFromCart) return; // Prevent action if disabled

    setIsRemovingFromCart(true); // Disable button
    setTimeout(() => setIsRemovingFromCart(false), 1000); // Re-enable after 1 second

    const tempCartItems =
      cartItemsContext.state !== null ? cartItemsContext.state : [];

    const indexOfProductInCart = tempCartItems.findIndex(
      (a) => a.id === product.id
    );

    if (indexOfProductInCart !== -1) {
      const products_ = [...tempCartItems];
      const currentQuantity = tempCartItems[indexOfProductInCart].quantity;

      if (currentQuantity > 1) {
        // Decrease quantity by 1
        products_.splice(indexOfProductInCart, 1);
        cartItemsContext.setState([
          { ...product, quantity: currentQuantity - 1 },
          ...products_,
        ]);
        snackbarMessage.current =
          "1 unit of " + product.title + " removed from cart";
      } else {
        // Remove item completely if quantity is 1
        products_.splice(indexOfProductInCart, 1);
        cartItemsContext.setState([...products_]);
        snackbarMessage.current = product.title + " removed from cart";
      }
      setSnackbarSeverity("error"); // Set red color for remove actions
      setOpen(true);
    }
  };
  const handleClose = () => {
    setOpen(false);
  };
  const handleLikeItem = () => {
    const likedItemsArray = likedItemsContext.state;
    if (likedItemsArray !== null) {
      const indexOfProductInLikedItems = likedItemsArray.findIndex(
        (a) => a.id === product.id
      );
      if (indexOfProductInLikedItems !== -1) {
        fadeOutOnRemovingLike
          ? setDeleteItemId(product.id)
          : setDeleteItemId(null);
        snackbarMessage.current = product.title + " removed from wishlist";

        setLiked(false);
        likedItemsArray.splice(indexOfProductInLikedItems, 1);
        setTimeout(() => {
          likedItemsContext.setState([...likedItemsArray]);
        }, 500);
        setSnackbarSeverity("error"); // Set red color for remove from wishlist
      } else {
        snackbarMessage.current = product.title + " added to wishlist";
        likedItemsContext.setState([product, ...likedItemsArray]);
        setLiked(true);
        setSnackbarSeverity("success"); // Set green color for add to wishlist
      }
    } else {
      snackbarMessage.current = product.title + " added to wishlist";
      likedItemsContext.setState([product]);
      setLiked(true);
      setSnackbarSeverity("success"); // Set green color for add to wishlist
    }
    if (!fadeOutOnRemovingLike) setOpen(true);
  };
  // Add new useEffect to track cart quantity
  useEffect(() => {
    const tempCartItems = cartItemsContext.state || [];
    const cartItem = tempCartItems.find((item) => item.id === product.id);
    setCartQuantity(cartItem ? cartItem.quantity : 0);
  }, [cartItemsContext.state, product.id]);

  const discountPercentage = Math.ceil(
    ((product.maxPrice - product.listPrice) / product.maxPrice) * 100
  );

  const savings = Math.ceil(product.maxPrice - product.listPrice);

  const stockCount = Number(product.unitsInStock);
  const hasStockInfo = Number.isFinite(stockCount);
  const outOfStock = hasStockInfo && stockCount <= 0;
  const lowStock = hasStockInfo && stockCount > 0 && stockCount <= 5;
  const reachedStockLimit = hasStockInfo && cartQuantity >= stockCount;

  return (
    <Fade in={deleteItemId !== product.id} timeout={500}>
      <Card
        sx={{
          position: "relative",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          borderRadius: compact ? "16px" : 3,
          overflow: "hidden",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          cursor: "pointer",
          backgroundColor: "#ffffff",
          ...(compact
            ? {
                border: "1px solid rgba(7, 94, 84, 0.08)",
                boxShadow:
                  "0 2px 10px rgba(16, 58, 53, 0.04), 0 1px 3px rgba(0,0,0,0.02)",
                "&:hover": {
                  boxShadow: "0 8px 24px rgba(16, 58, 53, 0.1)",
                  transform: "translateY(-3px)",
                },
              }
            : {
                border: "1px solid rgba(0, 0, 0, 0.06)",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.06)",
              }),
          // Disable touch highlight on mobile devices
          WebkitTapHighlightColor: "transparent",
          WebkitTouchCallout: "none",
          WebkitUserSelect: "none",
          KhtmlUserSelect: "none",
          MozUserSelect: "none",
          msUserSelect: "none",
          userSelect: "none",
          // Additional mobile touch fixes
          "@media (max-width: 768px)": {
            "&:focus": {
              outline: "none",
            },
            "&:active": {
              backgroundColor: "transparent",
            },
          },
          // Press feedback stands in for hover on touch screens
          "@media (hover: none)": {
            "&:active": {
              transform: "scale(0.985)",
            },
          },
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Savings / discount Badge */}
        {compact && discountPercentage > 0 && loaded && !outOfStock && (
          <Chip
            label={`${discountPercentage}% OFF`}
            size="small"
            sx={{
              position: "absolute",
              top: 8,
              left: 8,
              zIndex: 3,
              background: "linear-gradient(135deg, #075e54 0%, #05463f 100%)",
              color: "#ffffff",
              fontWeight: 800,
              fontSize: "0.65rem",
              height: 22,
              borderRadius: "6px",
              boxShadow: "0 2px 6px rgba(7, 94, 84, 0.25)",
              letterSpacing: "0.02em",
            }}
          />
        )}
        {compact && outOfStock && loaded && (
          <Chip
            label="Out of stock"
            size="small"
            sx={{
              position: "absolute",
              top: 8,
              left: 8,
              zIndex: 3,
              background: "#5b6b68",
              color: "white",
              fontWeight: 700,
              fontSize: "0.65rem",
              height: 22,
              borderRadius: "6px",
            }}
          />
        )}
        {!compact && !effectiveUseMobileSize && savings > 0 && loaded && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              zIndex: 3,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <Chip
              label={`Save ₹${savings}`}
              size="small"
              sx={{
                background: "linear-gradient(45deg, #4caf50, #2e7d32)",
                color: "white",
                fontWeight: 600,
                fontSize: "0.75rem",
                boxShadow: "0 2px 8px rgba(76, 175, 80, 0.3)",
                animation: "pulse-green 2s infinite",
                borderRadius: 0,
                opacity: 0.9,
              }}
            />
          </Box>
        )}

        {/* Quantity Badge */}
        {showQuantity && loaded && (
          <Chip
            label={`Qty: ${product.quantity}`}
            size="small"
            variant="outlined"
            sx={{
              position: "absolute",
              top: compact ? 8 : 12,
              right: showLikeButton ? (compact ? 44 : 60) : compact ? 8 : 12,
              zIndex: 2,
              backgroundColor: "rgba(255,255,255,0.9)",
              backdropFilter: "blur(10px)",
              fontWeight: 500,
              ...(compact && { height: 22, fontSize: "0.65rem" }),
            }}
          />
        )}

        {/* Like Button */}
        {showLikeButton && loaded && (
          <IconButton
            aria-label={liked ? "Remove from wishlist" : "Add to wishlist"}
            onClick={(e) => {
              e.preventDefault();
              handleLikeItem();
            }}
            sx={{
              position: "absolute",
              top: compact ? 8 : 8,
              right: compact ? 8 : 8,
              zIndex: 3,
              backgroundColor: "rgba(255,255,255,0.92)",
              backdropFilter: "blur(8px)",
              width: compact ? 32 : 40,
              height: compact ? 32 : 40,
              border: "1px solid rgba(255,255,255,0.7)",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
              transition: "all 0.25s ease",
              "&:hover": {
                backgroundColor: "#ffffff",
                transform: "scale(1.08)",
              },
            }}
          >
            {liked ? (
              <FavoriteIcon sx={{ color: "#e11d48", fontSize: compact ? 17 : 20 }} />
            ) : (
              <FavoriteBorderIcon
                sx={{ color: "#64748b", fontSize: compact ? 17 : 20 }}
              />
            )}
          </IconButton>
        )}

        {/* Product Image */}
        <Box
          component={Link}
          to={link}
          sx={{
            position: "relative",
            height: compact ? { xs: 168, sm: 185 } : 220,
            overflow: "hidden",
            textDecoration: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: compact
              ? "linear-gradient(180deg, #fbfcfb 0%, #f4f7f6 100%)"
              : "#ffffff",
            opacity: outOfStock ? 0.65 : 1,
            transition: "opacity 0.2s ease",
          }}
        >
          {showCarousel && loaded ? (
            <ImageCarousel
              showIndicators={false}
              ImagesArr={product.images.map((a) => a.url)}
              autoPlayOnHover={true}
              sx={{ width: "100%", height: "100%" }}
            />
          ) : loaded ? (
            <CardMedia
              component="img"
              image={product.images[0]?.url}
              alt={product.title}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                p: compact ? 1 : 0,
                transition: "transform 0.35s ease",
                transform: isHovered && !compact ? "scale(1.05)" : "scale(1)",
              }}
            />
          ) : (
            <Skeleton
              variant="rectangular"
              width="100%"
              height="100%"
              sx={{ transform: "scale(1)" }}
            />
          )}
        </Box>

        {/* Product Details */}
        <CardContent
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            gap: compact ? 0.5 : 1,
            p: compact ? "10px 10px 12px !important" : 2,
            "&:last-child": { pb: compact ? "12px !important" : 2 },
          }}
        >
          {/* Product Title */}
          <Box component={Link} to={link} sx={{ textDecoration: "none" }}>
            <Typography
              variant={effectiveUseMobileSize ? "body2" : "body1"}
              sx={{
                fontWeight: 600,
                fontSize: compact ? "0.82rem" : undefined,
                color: "#143833",
                lineHeight: 1.3,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                minHeight: compact ? "2.6em" : undefined,
              }}
            >
              {loaded ? product.title : <Skeleton />}
            </Typography>
          </Box>

          {/* Rating */}
          {showRating && (
            <Box sx={{ display: "flex", alignItems: "center", gap: compact ? 0.4 : 1 }}>
              {loaded ? (
                compact ? (
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.35,
                      px: 0.65,
                      py: 0.15,
                      borderRadius: "6px",
                      backgroundColor: "#f0fdf4",
                      border: "1px solid #dcfce7",
                    }}
                  >
                    <StarIcon sx={{ fontSize: 13, color: "#eab308" }} />
                    <Typography
                      component="span"
                      sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#166534" }}
                    >
                      {rating.avg > 0 ? rating.avg : "New"}
                    </Typography>
                    {rating.total > 0 && (
                      <Typography
                        component="span"
                        sx={{ fontSize: "0.68rem", color: "#15803d", fontWeight: 500 }}
                      >
                        ({rating.total})
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <StarRating
                    rating={rating.avg}
                    showTotalNumber={rating.total}
                    showRatingNumber={showRatingNumber}
                  />
                )
              ) : (
                <Skeleton width="60%" height={20} />
              )}
            </Box>
          )}

          {/* Price Section */}
          <Box sx={{ mt: "auto", pt: compact ? 0.25 : 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                gap: compact ? 0.6 : 1,
                mb: compact ? 0.25 : 0.5,
                flexWrap: "wrap",
              }}
            >
              <Typography
                variant={effectiveUseMobileSize ? "h6" : "h5"}
                sx={{
                  fontWeight: 800,
                  color: "#075e54",
                  fontSize: compact ? "1.05rem" : undefined,
                  letterSpacing: "-0.01em",
                }}
              >
                {loaded ? `₹${product.listPrice}` : <Skeleton width="40%" />}
              </Typography>

              {!showQuantity &&
                loaded &&
                product.maxPrice > product.listPrice && (
                  <Typography
                    variant="body2"
                    sx={{
                      textDecoration: "line-through",
                      color: "#94a3b8",
                      fontWeight: 500,
                      fontSize: compact ? "0.75rem" : undefined,
                    }}
                  >
                    ₹{product.maxPrice}
                  </Typography>
                )}
              {/* Discount Percentage Badge on Desktop */}
              {!compact && !effectiveUseMobileSize && discountPercentage > 0 && loaded && (
                <Chip
                  label={`${discountPercentage}% OFF`}
                  size="small"
                  sx={{
                    background: "linear-gradient(45deg, #ff6b6b, #ee5a24)",
                    color: "white",
                    fontWeight: 600,
                    fontSize: "0.7rem",
                    height: "20px",
                    borderRadius: "10px",
                    boxShadow: "0 2px 8px rgba(255, 107, 107, 0.3)",
                    animation: "pulse-red 2s infinite",
                  }}
                />
              )}
            </Box>
            {compact && loaded && (lowStock || outOfStock) && (
              <Typography
                sx={{
                  mt: 0.25,
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  color: outOfStock ? "#8a9995" : "#c2410c",
                }}
              >
                {outOfStock ? "Currently unavailable" : `Only ${stockCount} left`}
              </Typography>
            )}
            {!compact && effectiveUseMobileSize && savings > 0 && loaded && (
              <Box
                sx={{
                  zIndex: 3,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Chip
                  label={`Save ₹${savings}`}
                  size="small"
                  sx={{
                    background: "linear-gradient(45deg, #4caf50, #2e7d32)",
                    color: "white",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    boxShadow: "0 2px 8px rgba(76, 175, 80, 0.3)",
                    animation: "pulse-green 2s infinite",
                  }}
                />
              </Box>
            )}
            {showQuantity && loaded && (
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  textDecoration: "line-through",
                }}
              >
                ₹{product.maxPrice}
              </Typography>
            )}
          </Box>

          {/* Add to Cart Controls - Moved below price section */}
          {addToCartButton && loaded && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: useMobileSize || compact ? 0.5 : 2,
                mt: compact ? 0.75 : 1.5,
              }}
            >
              {cartQuantity > 0 ? (
                // Show add/remove icons when item is in cart
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 0.5,
                    width: "100%",
                    padding: compact ? "0 4px" : "2px 6px",
                    border: "1.5px solid #075e54",
                    borderRadius: "999px",
                    height: compact ? 34 : 38,
                    backgroundColor: "#f0fdf4",
                    boxShadow: "0 2px 8px rgba(7, 94, 84, 0.12)",
                    transition: "all 0.25s ease",
                  }}
                >
                  {/* Remove button */}
                  <IconButton
                    aria-label={
                      cartQuantity > 1 ? "Remove one unit" : "Remove from cart"
                    }
                    disabled={isRemovingFromCart}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!isRemovingFromCart) {
                        handleRemoveFromCart();
                      }
                    }}
                    sx={{
                      color: cartQuantity === 1 ? "#ef4444" : "#075e54",
                      width: compact ? 28 : 30,
                      height: compact ? 28 : 30,
                      p: 0,
                      transition: "all 0.2s ease",
                      cursor: isRemovingFromCart ? "not-allowed" : "pointer",
                      "&:hover": {
                        backgroundColor:
                          cartQuantity === 1
                            ? "rgba(239, 68, 68, 0.12)"
                            : "rgba(7, 94, 84, 0.1)",
                        transform: "scale(1.08)",
                      },
                      "&:active": {
                        transform: "scale(0.92)",
                      },
                    }}
                  >
                    {isRemovingFromCart ? (
                      <CircularProgress
                        size={effectiveUseMobileSize ? 14 : 16}
                        sx={{ color: cartQuantity === 1 ? "#ef4444" : "#075e54" }}
                      />
                    ) : (
                      <RemoveIcon
                        sx={{ fontSize: effectiveUseMobileSize ? 16 : 18 }}
                      />
                    )}
                  </IconButton>

                  {/* Quantity Display */}
                  <Typography
                    variant={effectiveUseMobileSize ? "body2" : "body1"}
                    sx={{
                      fontWeight: 800,
                      minWidth: compact ? "14px" : "36px",
                      textAlign: "center",
                      fontSize:
                        compact || effectiveUseMobileSize ? "0.82rem" : "0.88rem",
                      color: "#075e54",
                      letterSpacing: "0.2px",
                      userSelect: "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.5,
                    }}
                  >
                    {cartQuantity}
                    {!effectiveUseMobileSize && !compact && (
                      <Typography
                        component="span"
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          color: "#0a6157",
                          letterSpacing: 0,
                        }}
                      >
                        in cart
                      </Typography>
                    )}
                  </Typography>

                  {/* Add button */}
                  <IconButton
                    aria-label="Add one more unit"
                    disabled={isAddingToCart || reachedStockLimit}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleAddToCart();
                    }}
                    sx={{
                      color: reachedStockLimit ? "#94a3b8" : "#075e54",
                      width: compact ? 28 : 30,
                      height: compact ? 28 : 30,
                      p: 0,
                      transition: "all 0.2s ease",
                      cursor:
                        isAddingToCart || reachedStockLimit
                          ? "not-allowed"
                          : "pointer",
                      "&:hover": {
                        backgroundColor: reachedStockLimit
                          ? "transparent"
                          : "rgba(7, 94, 84, 0.1)",
                        transform: reachedStockLimit ? "none" : "scale(1.08)",
                      },
                      "&:active": {
                        transform: reachedStockLimit ? "none" : "scale(0.92)",
                      },
                    }}
                  >
                    {isAddingToCart ? (
                      <CircularProgress
                        size={effectiveUseMobileSize ? 14 : 16}
                        sx={{ color: "#075e54" }}
                      />
                    ) : (
                      <AddIcon
                        sx={{ fontSize: effectiveUseMobileSize ? 16 : 18 }}
                      />
                    )}
                  </IconButton>
                </Box>
              ) : (
                // Show "Add to Cart" button when item is not in cart
                <CustomButton
                  variant="contained"
                  size="small"
                  disabled={isAddingToCart || outOfStock}
                  fullWidth
                  startIcon={
                    outOfStock ? null : isAddingToCart ? (
                      <CircularProgress
                        size={effectiveUseMobileSize ? 14 : 16}
                        sx={{ color: "rgba(255, 255, 255, 0.85)" }}
                      />
                    ) : (
                      <ShoppingCartIcon
                        sx={{ fontSize: effectiveUseMobileSize ? 16 : 18 }}
                      />
                    )
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleAddToCart();
                  }}
                  sx={{
                    fontWeight: 700,
                    borderRadius: "999px",
                    minHeight: compact ? 34 : 38,
                    height: compact ? 34 : 38,
                    py: 0,
                    px: compact ? 1.5 : 2,
                    textTransform: "none",
                    letterSpacing: "0.3px",
                    fontSize:
                      compact || effectiveUseMobileSize ? "0.78rem" : "0.86rem",
                    background: outOfStock
                      ? "rgba(0, 0, 0, 0.08)"
                      : "linear-gradient(135deg, #075e54 0%, #0d7064 100%)",
                    backgroundColor: outOfStock
                      ? "rgba(0, 0, 0, 0.08)"
                      : "#075e54",
                    borderColor: "transparent",
                    color: outOfStock ? "rgba(0, 0, 0, 0.38)" : "#ffffff",
                    boxShadow: outOfStock
                      ? "none"
                      : "0 3px 10px rgba(7, 94, 84, 0.24)",
                    "& .MuiTypography-root": {
                      fontSize:
                        compact || effectiveUseMobileSize
                          ? "0.78rem"
                          : "0.86rem",
                      fontWeight: 700,
                      lineHeight: 1,
                      letterSpacing: "0.3px",
                    },
                    "& .MuiButton-startIcon": {
                      marginRight: "6px",
                      "& svg": {
                        fontSize: `${
                          compact || effectiveUseMobileSize ? 15 : 18
                        }px !important`,
                      },
                    },
                    "&:hover": {
                      background: outOfStock
                        ? "rgba(0, 0, 0, 0.08)"
                        : "linear-gradient(135deg, #05463f 0%, #0a5d53 100%)",
                      backgroundColor: outOfStock
                        ? "rgba(0, 0, 0, 0.08)"
                        : "#05463f",
                      boxShadow: outOfStock
                        ? "none"
                        : "0 6px 16px rgba(7, 94, 84, 0.35)",
                      transform: outOfStock ? "none" : "translateY(-1px)",
                    },
                    "&:active": {
                      transform: outOfStock ? "none" : "scale(0.98)",
                      boxShadow: outOfStock
                        ? "none"
                        : "0 2px 6px rgba(7, 94, 84, 0.2)",
                    },
                    "&:disabled": {
                      backgroundColor: "#f1f5f9",
                      color: "#94a3b8",
                      borderColor: "transparent",
                      boxShadow: "none",
                      cursor: "not-allowed",
                    },
                    cursor: isAddingToCart ? "not-allowed" : "pointer",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                >
                  {outOfStock
                    ? "Out of stock"
                    : isAddingToCart
                    ? "Adding..."
                    : effectiveUseMobileSize
                    ? "Add"
                    : "Add to Cart"}
                </CustomButton>
              )}
            </Box>
          )}
        </CardContent>

        {/* <CustomisedSnackbar
          message={snackbarMessage.current}
          severity={snackbarSeverity} // Use dynamic severity instead of hardcoded "success"
          open={open}
          handleClose={handleClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          autoHideDuration={4000}
        /> */}
      </Card>
    </Fade>
  );
};

export default ProductCard;
